import React, { useEffect, useState } from "react";
import { Save, Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function AssistantAdmin() {
  const faqApi = adminApi.assistantFaqs();
  const [instructions, setInstructions] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ pregunta: "", respuesta: "" });
  const [toDelete, setToDelete] = useState(null);

  const loadFaqs = () => {
    setLoading(true);
    faqApi.list().then(setFaqs).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };

  useEffect(() => {
    adminApi.getAssistantConfig().then((d) => setInstructions(d.instructions || "")).catch(() => {});
    loadFaqs();
    // eslint-disable-next-line
  }, []);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await adminApi.updateAssistantConfig(instructions);
      toast.success("Instrucciones del asistente actualizadas");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingCfg(false); }
  };

  const openCreate = () => { setEditing(null); setForm({ pregunta: "", respuesta: "" }); };
  const openEdit = (f) => { setEditing(f); setForm({ pregunta: f.pregunta, respuesta: f.respuesta }); };
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => { if (editing !== undefined) {} }, [editing]);

  const startCreate = () => { openCreate(); setDialogOpen(true); };
  const startEdit = (f) => { openEdit(f); setDialogOpen(true); };

  const saveFaq = async () => {
    if (!form.pregunta.trim() || !form.respuesta.trim()) {
      toast.error("Completa la pregunta y la respuesta.");
      return;
    }
    try {
      if (editing) await faqApi.update(editing.id, form);
      else await faqApi.create(form);
      toast.success("Guardado");
      setDialogOpen(false);
      loadFaqs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try {
      await faqApi.remove(toDelete.id);
      toast.success("Eliminado");
      setToDelete(null);
      loadFaqs();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#0A1C30] flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-gold" />
        </span>
        <div>
          <h1 className="text-3xl font-extrabold text-[#0A1C30]">Asistente IA</h1>
          <p className="text-[#6B7688]">Entrena al asistente que responde en la portada.</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-white rounded-2xl ring-1 ring-black/5 p-6">
        <h2 className="text-lg font-extrabold text-[#0A1C30]">Personalidad e instrucciones</h2>
        <p className="text-sm text-[#6B7688] mt-1">Define quién es el asistente, su tono y qué debe responder.</p>
        <Textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          className="mt-4 min-h-[160px]"
          placeholder="Eres el asistente de Supera Leadership..."
        />
        <button onClick={saveConfig} disabled={savingCfg} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-2.5 transition-colors disabled:opacity-60">
          <Save className="w-4 h-4" /> {savingCfg ? "Guardando..." : "Guardar instrucciones"}
        </button>
      </div>

      {/* Knowledge base */}
      <div className="mt-8 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-[#0A1C30]">Base de conocimiento (FAQ)</h2>
          <p className="text-sm text-[#6B7688]">Preguntas y respuestas que el asistente usará para responder.</p>
        </div>
        <button onClick={startCreate} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-3 transition-colors">
          <Plus className="w-5 h-5" /> Añadir
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {loading && <p className="text-[#6B7688] text-sm">Cargando...</p>}
        {!loading && faqs.length === 0 && <p className="text-[#6B7688] text-sm">Sin preguntas aún.</p>}
        {faqs.map((f) => (
          <div key={f.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5 flex items-start justify-between gap-4">
            <div>
              <p className="font-bold text-[#0A1C30]">{f.pregunta}</p>
              <p className="text-sm text-[#6B7688] mt-1">{f.respuesta}</p>
            </div>
            <div className="flex-shrink-0 flex">
              <button onClick={() => startEdit(f)} className="w-9 h-9 rounded-lg hover:bg-[#EDF0F4] text-[#3B7DF6] flex items-center justify-center transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => setToDelete(f)} className="w-9 h-9 rounded-lg hover:bg-[#FCE4E4] text-[#E23B3B] flex items-center justify-center transition-colors ml-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">{editing ? "Editar" : "Añadir"} pregunta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Pregunta</Label>
              <Input value={form.pregunta} onChange={(e) => setForm((s) => ({ ...s, pregunta: e.target.value }))} placeholder="¿Qué es Supera?" />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Respuesta</Label>
              <Textarea value={form.respuesta} onChange={(e) => setForm((s) => ({ ...s, respuesta: e.target.value }))} className="min-h-[120px]" placeholder="Supera es..." />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="rounded-xl px-5 py-2.5 font-bold text-[#6B7688] hover:bg-[#EDF0F4] transition-colors">Cancelar</button>
            <button onClick={saveFaq} className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-6 py-2.5 transition-colors">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta pregunta?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#E23B3B] hover:bg-[#c72f2f]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
